import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { AtSign, ExternalLink, Loader2, Reply } from "lucide-react";

import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { identidadeDaRede } from "./redes";
import Cabecalho from "./Cabecalho";

// Quem citou suas contas.
//
// A resposta sai como comentário na publicação onde a menção aconteceu — não
// abre conversa. Por isso o texto do botão diz "Responder na publicação": quem
// espera abrir um atendimento com a pessoa ficaria esperando por algo que não
// vai acontecer.

const formatarData = (valor) => {
  if (!valor) return "";

  try {
    return new Date(valor).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return "";
  }
};

const Mencoes = () => {
  const [mencoes, setMencoes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [respostas, setRespostas] = useState({});
  const [enviando, setEnviando] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);

    try {
      const { data } = await api.get("/zernio/mentions");
      setMencoes(data.data || []);
    } catch (err) {
      toastError(err);
    }

    setCarregando(false);
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const responder = async (mencao) => {
    const texto = (respostas[mencao.id] || "").trim();

    if (!texto) {
      toast.error("Escreva a resposta.");
      return;
    }

    setEnviando(mencao.id);

    try {
      await api.post("/zernio/mentions/reply", {
        accountId: mencao.accountId,
        mediaId: mencao.mediaId || mencao.postId,
        commentId: mencao.commentId,
        message: texto
      });

      toast.success("Resposta publicada.");
      setRespostas((atuais) => ({ ...atuais, [mencao.id]: "" }));
      await carregar();
    } catch (err) {
      toastError(err);
    }

    setEnviando(null);
  };

  return (
    <div className="space-y-4 p-4">
      <Cabecalho
        titulo="Menções"
        descricao="Quem citou suas contas em publicações e comentários. A resposta sai como comentário na publicação, não como conversa."
      />

      {carregando ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : mencoes.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed px-6 py-12 text-center">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <AtSign className="h-5 w-5" />
          </span>
          <p className="mt-3 text-sm font-medium">Ninguém citou você ainda</p>
          <p className="mt-1 max-w-md text-sm text-muted-foreground">
            As menções aparecem aqui quando alguém marca sua conta numa
            publicação ou comentário. Hoje só o Instagram e o LinkedIn avisam a
            Zernio sobre isso.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {mencoes.map((mencao) => {
            const rede = identidadeDaRede(mencao.platform);
            const Glifo = rede.icone;

            return (
              <div key={mencao.id} className="rounded-xl border bg-card p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-white"
                    style={{ backgroundColor: rede.corSelo }}
                  >
                    <Glifo className="h-3.5 w-3.5" />
                  </span>
                  <span className="text-sm font-medium">
                    {mencao.from?.username || mencao.username || "Alguém"}
                  </span>
                  <Badge variant="secondary">{rede.rotulo}</Badge>
                  <span className="text-xs text-muted-foreground">
                    {formatarData(mencao.publishedAt || mencao.createdTime)}
                  </span>

                  {mencao.permalink && (
                    <a
                      href={mencao.permalink}
                      target="_blank"
                      rel="noreferrer"
                      className="ml-auto inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    >
                      <ExternalLink className="h-3 w-3" />
                      Ver na rede
                    </a>
                  )}
                </div>

                <p className="mt-2 whitespace-pre-wrap break-words text-sm">
                  {mencao.text || mencao.caption || "(sem texto)"}
                </p>

                <Textarea
                  rows={2}
                  className="mt-2"
                  placeholder="Responder na publicação…"
                  value={respostas[mencao.id] || ""}
                  onChange={(e) =>
                    setRespostas((atuais) => ({
                      ...atuais,
                      [mencao.id]: e.target.value
                    }))
                  }
                />

                <Button
                  size="sm"
                  className="mt-2"
                  onClick={() => responder(mencao)}
                  disabled={enviando !== null}
                >
                  {enviando === mencao.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Reply className="h-4 w-4" />
                  )}
                  Responder na publicação
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Mencoes;
